// TRANK zastávka — nakládka i vykládka sdílí tuto obrazovku.
// is_unloading=false → skenuj pečeti u zdrojového depa
// is_unloading=true  → potvrď vyložení v Brně (pečetě = co bylo naloženo)

import { useCallback, useEffect, useState } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonPage,
  IonSpinner,
  IonTitle,
  IonToolbar,
  useIonAlert,
} from "@ionic/react";
import type { CollectionExpected, CollectionExpectedItem } from "@routera/shared";
import { api } from "../lib/api";
import { useShiftStore } from "../store/shiftStore";
import { BarcodeScanner } from "../components/BarcodeScanner";
import { openNavigationSearch } from "../lib/navigation";

// Navigace ma jedno hrdlo v `lib/navigation.ts` — tady byla ctvrta kopie URL, ktera
// se uz stihla rozejit (Waze bez `navigate=yes`, Google jen `search/`).

export function CollectionStopScreen() {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const rid = new URLSearchParams(location.search).get("rid");
  const backUrl = rid ? `/route?rid=${rid}` : "/route";
  const qc = useQueryClient();
  const [presentAlert] = useIonAlert();
  const shift = useShiftStore((s) => s.shift);
  const shiftLoading = useShiftStore((s) => s.loading);
  const refreshShift = useShiftStore((s) => s.refresh);

  // Po restartu appky (návrat na úkol přes uložený deep-link, typicky po pádu
  // kvůli kameře) je store se směnou prázdný — dotáhnout hned, ne až při odeslání.
  useEffect(() => {
    if (!shift?.id) void refreshShift();
  }, [shift?.id, refreshShift]);

  // Skeny přežijí odskok na zahájení směny i restart obrazovky (case Cach 25.8.:
  // řidič celý TRANK naskenoval BEZ zahájené směny a uložení nemělo na co sáhnout).
  const [scanned, setScanned] = useState<string[]>(() => {
    try { return JSON.parse(sessionStorage.getItem(`trank-scan-${id}`) || "[]"); }
    catch { return []; }
  });
  useEffect(() => {
    try { sessionStorage.setItem(`trank-scan-${id}`, JSON.stringify(scanned)); } catch {}
  }, [id, scanned]);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data, isLoading, isError } = useQuery<CollectionExpected>({
    queryKey: ["collection", id],
    queryFn: () => api.shift.collectionExpected(id),
  });

  useEffect(() => {
    if (data?.completed && data.scanned.length > 0) setScanned(data.scanned);
  }, [data]);

  const handleScan = useCallback((code: string) => {
    setScanned((prev) => prev.includes(code) ? prev : [...prev, code]);
  }, []);

  const isUnloading = data?.is_unloading ?? false;
  // Ohlášené pečetě jsou jen informační (jako v počítárně) — řidič může špatnou
  // schovat křížkem a evidovat to, co fakt nakládá. Schované nevstupují do počtů.
  const hidden = data?.hidden ?? [];
  const expectedSeals = (data?.expected ?? [])
    .map((e) => e.seal)
    .filter((s) => !hidden.includes(s));
  const matched = scanned.filter((s) => expectedSeals.includes(s));
  const extra = scanned.filter((s) => !expectedSeals.includes(s));
  const missing = expectedSeals.filter((s) => !scanned.includes(s));

  // Destinace pro navigaci
  const navQuery = isUnloading
    ? "Brno"
    : [...new Set((data?.expected ?? []).map((e) => e.source_depot).filter(Boolean))].join(", ") || "depo";

  // Skupiny pro zobrazení (loading: podle řidiče; unloading: jen "Naloženo")
  const byGroup = (data?.expected ?? []).reduce<Record<string, CollectionExpectedItem[]>>((acc, item) => {
    const key = isUnloading ? "Naložené pečeti" : item.driver_name;
    (acc[key] ??= []).push(item);
    return acc;
  }, {});

  const handleComplete = async () => {
    // Směna může ve store chybět (restart appky / výpadek při hydrataci) —
    // zkusit ji donačíst TEĎ místo slepé chyby; naskenované pečetě nezahazovat.
    let shiftId = shift?.id;
    if (!shiftId) {
      await refreshShift();
      shiftId = useShiftStore.getState().shift?.id;
    }
    if (!shiftId) {
      presentAlert({
        header: "Směna není zahájená",
        message: "Nejdřív na úvodní obrazovce zahaj směnu a pak se sem vrať — naskenované pečetě zůstávají uložené.",
        buttons: [
          { text: "Zůstat tady", role: "cancel" },
          { text: "Zahájit směnu", handler: () => history.push("/") },
        ],
      });
      return;
    }
    setSaving(true);
    try {
      await api.shift.collectionComplete(id, shiftId, scanned);
      try { sessionStorage.removeItem(`trank-scan-${id}`); } catch {}
      await qc.invalidateQueries({ queryKey: ["collection", id] });
      await qc.invalidateQueries({ queryKey: ["trank", "today"] });
      // Ať hotový úkol hned zmizí ze seznamu (server posílá `done` v /route/today).
      await qc.invalidateQueries({ queryKey: ["mobile", "route", "today"] });
      history.replace(backUrl);
    } catch (e: any) {
      presentAlert({ header: "Chyba", message: e?.message ?? "Uložení selhalo", buttons: ["OK"] });
    } finally {
      setSaving(false);
    }
  };

  // Ohlášené pečeti jsou jen KONTROLNÍ — řidič nakládá to, co reálně veze, a nesoulad
  // s tím, co ohlásili řidiči zdrojových tras, nesmí být překážka. Dřív tu svítilo
  // červené „Chybí N", tlačítko zůstalo modré a jmenovalo se „Dokončit (chybí 3)",
  // takže to vypadalo jako chyba, kterou musí vyřešit — přitom projít to šlo vždycky.
  const btnLabel = isUnloading ? "✓ Vyložit v Brně" : "✓ Dokončit nakládku";

  // Schování je serverové (vratné) — přežije restart appky a je evidované.
  const hideSeal = async (seal: string) => {
    setScanned((p) => p.filter((x) => x !== seal));
    try {
      await api.shift.collectionSealHide(id, seal);
      await qc.invalidateQueries({ queryKey: ["collection", id] });
    } catch (e: any) {
      presentAlert({ header: "Chyba", message: e?.message ?? "Nepodařilo se schovat pečeť", buttons: ["OK"] });
    }
  };
  const unhideSeal = async (seal: string) => {
    try {
      await api.shift.collectionSealUnhide(id, seal);
      await qc.invalidateQueries({ queryKey: ["collection", id] });
    } catch (e: any) {
      presentAlert({ header: "Chyba", message: e?.message ?? "Nepodařilo se vrátit pečeť", buttons: ["OK"] });
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref={backUrl} />
          </IonButtons>
          <IonTitle>{data?.name ?? "TRANK"}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        {isLoading ? (
          <div style={{ textAlign: "center", padding: 60 }}>
            <IonSpinner name="crescent" />
          </div>
        ) : isError ? (
          <div style={{ padding: 24 }}>
            <p style={{ color: "rgb(var(--c-danger))" }}>Nepodařilo se načíst data.</p>
          </div>
        ) : (
          <div style={{ padding: "16px 12px" }}>

            {/* Bez zahájené směny jde skenovat, ale NE uložit — říct to HNED,
                ne až po 20 minutách práce (case Cach 25.8.). */}
            {!shift?.id && !shiftLoading && (
              <div style={{
                background: "rgb(var(--c-warning) / 0.12)",
                border: "1px solid rgb(var(--c-warning) / 0.45)",
                borderRadius: 14, padding: "12px 14px", marginBottom: 12,
              }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "rgb(var(--c-warning))" }}>
                  Směna není zahájená
                </div>
                <div style={{ fontSize: 12.5, color: "rgb(var(--c-text-soft))", marginTop: 3, lineHeight: 1.45 }}>
                  Skenovat můžeš, ale dokončení nepůjde uložit. Zahaj směnu — naskenované pečetě tu zůstanou.
                </div>
                <button
                  onClick={() => history.push("/")}
                  style={{
                    marginTop: 10, padding: "9px 16px", borderRadius: 10, border: "none",
                    background: "rgb(var(--c-warning))", color: "#241A05", fontWeight: 700, fontSize: 13.5,
                  }}
                >
                  Zahájit směnu
                </button>
              </div>
            )}

            {/* Navigační karta */}
            <div style={{
              background: isUnloading ? "rgb(var(--c-accent) / 0.08)" : "rgb(var(--c-surface))",
              border: `1px solid ${isUnloading ? "rgb(var(--c-accent) / 0.3)" : "rgb(var(--c-border))"}`,
              borderRadius: 14,
              padding: "16px 16px 14px",
              marginBottom: 12,
            }}>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, color: "rgb(var(--c-muted))", marginBottom: 4 }}>
                {isUnloading ? "Destinace — vyložení" : "Nakládka u depa"}
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "rgb(var(--c-text))", marginBottom: 12 }}>
                {isUnloading ? "Brno" : navQuery}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                {(["mapy", "google", "waze"] as const).map((provider) => (
                  <button
                    key={provider}
                    onClick={() => openNavigationSearch(navQuery, provider)}
                    style={{
                      padding: "10px 6px",
                      background: "transparent",
                      border: "1.5px solid rgb(var(--c-border-strong))",
                      borderRadius: 10,
                      fontSize: 12,
                      fontWeight: 600,
                      color: "rgb(var(--c-accent))",
                      cursor: "pointer",
                      textAlign: "center",
                    }}
                  >
                    {provider === "mapy" ? "Mapy.cz" : provider === "google" ? "Google" : "Waze"}
                  </button>
                ))}
              </div>
            </div>

            {/* Progress header */}
            <div style={{
              background: "rgb(var(--c-surface))",
              border: "1px solid rgb(var(--c-border))",
              borderRadius: 12,
              padding: "14px 16px",
              marginBottom: 12,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}>
              <div>
                <div style={{ fontSize: 13, color: "rgb(var(--c-muted))" }}>
                  {isUnloading ? "Vyloženo" : "Naloženo"}
                </div>
                {/* Hlavní číslo = co řidič reálně naskenoval. Dřív tu bylo
                    „naskenováno / očekáváno" (např. 3/7), což vypadalo jako
                    nesplněný úkol, i když nakládal správně to, co veze. */}
                <div style={{ fontSize: 22, fontWeight: 700, color: "rgb(var(--c-text))" }}>
                  {scanned.length}
                </div>
                {expectedSeals.length > 0 && (
                  <div style={{ fontSize: 11, color: "rgb(var(--c-muted))", marginTop: 2 }}>
                    {matched.length} z {expectedSeals.length} ohlášených
                  </div>
                )}
              </div>
              {data?.completed && (
                <div style={{
                  padding: "4px 12px",
                  background: "rgb(var(--c-success) / 0.12)",
                  border: "1px solid rgb(var(--c-success) / 0.4)",
                  borderRadius: 20,
                  fontSize: 12,
                  fontWeight: 700,
                  color: "rgb(var(--c-success))",
                }}>
                  ✓ Dokončeno
                </div>
              )}
            </div>

            {/* Scanner */}
            <div style={{
              background: "rgb(var(--c-surface))",
              border: "1px solid rgb(var(--c-border))",
              borderRadius: 12,
              padding: "14px 16px",
              marginBottom: 12,
            }}>
              <button
                onClick={() => setScannerOpen(true)}
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  background: "transparent",
                  border: "1.5px solid rgb(var(--c-border-strong))",
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: 600,
                  color: "rgb(var(--c-text-soft))",
                  cursor: "pointer",
                }}
              >
                📷 Skenovat pečeť
              </button>

              {scanned.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
                  {scanned.map((s) => {
                    const isMatched = expectedSeals.includes(s);
                    return (
                      <button
                        key={s}
                        onClick={() => setScanned((p) => p.filter((x) => x !== s))}
                        title="Odebrat"
                        style={{
                          padding: "3px 10px",
                          // Neohlášená pečeť není chyba — řidič veze, co veze. Proto
                          // neutrální styl, ne varovná oranžová jako dřív.
                          background: isMatched ? "rgb(var(--c-success) / 0.12)" : "rgb(var(--c-elevated))",
                          border: `1px solid ${isMatched ? "rgb(var(--c-success) / 0.4)" : "rgb(var(--c-border))"}`,
                          borderRadius: 20,
                          fontSize: 12,
                          fontWeight: 600,
                          fontFamily: "monospace",
                          color: isMatched ? "rgb(var(--c-success))" : "rgb(var(--c-text-soft))",
                          cursor: "pointer",
                        }}
                      >
                        {s} ×
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Skupiny pečetí */}
            {Object.entries(byGroup).map(([groupName, items]) => (
              <div key={groupName} style={{
                background: "rgb(var(--c-surface))",
                border: "1px solid rgb(var(--c-border))",
                borderRadius: 12,
                padding: "12px 16px",
                marginBottom: 8,
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "rgb(var(--c-text))", marginBottom: 8 }}>
                  {groupName}
                  {!isUnloading && (
                    <span style={{ fontSize: 11, fontWeight: 400, color: "rgb(var(--c-muted))", marginLeft: 6 }}>
                      {items[0].source_depot ?? ""}
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {items.map((item) => {
                    // Schovaná ohlášená pečeť: přeškrtnutá, ťuknutím se vrátí.
                    if (hidden.includes(item.seal)) {
                      return (
                        <button
                          key={item.seal}
                          type="button"
                          onClick={() => void unhideSeal(item.seal)}
                          title="Vrátit schovanou pečeť"
                          style={{
                            padding: "3px 10px",
                            font: "inherit",
                            background: "transparent",
                            border: "1px dashed rgb(var(--c-border))",
                            borderRadius: 20,
                            fontSize: 12,
                            fontFamily: "monospace",
                            color: "rgb(var(--c-muted))",
                            fontWeight: 600,
                            textDecoration: "line-through",
                            cursor: "pointer",
                          }}>
                          {item.seal} ↩
                        </button>
                      );
                    }
                    const isFound = scanned.includes(item.seal);
                    return (
                      // Ruční odškrtnutí ťuknutím = záloha pro nečitelnou pečeť;
                      // křížek pečeť SCHOVÁ (je jen informační — evidence toho,
                      // co řidič fakt nakládá, jsou naskenované/ťuknuté pečetě).
                      <div
                        key={item.seal}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          background: isFound ? "rgb(var(--c-success) / 0.12)" : "rgb(var(--c-elevated))",
                          border: `1px solid ${isFound ? "rgb(var(--c-success) / 0.4)" : "rgb(var(--c-border))"}`,
                          borderRadius: 20,
                          transition: "all 0.15s",
                        }}>
                        <button
                          type="button"
                          onClick={() => setScanned((p) =>
                            p.includes(item.seal) ? p.filter((x) => x !== item.seal) : [...p, item.seal])}
                          style={{
                            padding: "3px 2px 3px 10px",
                            font: "inherit",
                            background: "transparent",
                            border: "none",
                            fontSize: 12,
                            fontFamily: "monospace",
                            color: isFound ? "rgb(var(--c-success))" : "rgb(var(--c-muted))",
                            fontWeight: 600,
                            cursor: "pointer",
                          }}>
                          {isFound ? "✓ " : ""}{item.seal}
                        </button>
                        {!isUnloading && (
                          <button
                            type="button"
                            onClick={() => void hideSeal(item.seal)}
                            title="Schovat — tahle pečeť tu není"
                            style={{
                              padding: "3px 9px 3px 5px",
                              background: "transparent",
                              border: "none",
                              color: "rgb(var(--c-muted))",
                              fontSize: 13,
                              lineHeight: 1,
                              cursor: "pointer",
                            }}>
                            ×
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {expectedSeals.length === 0 && (
              <div style={{ padding: 16, textAlign: "center", color: "rgb(var(--c-muted))", fontSize: 13 }}>
                {isUnloading
                  ? "Nejprve dokončete nakládku — pečeti se načtou automaticky."
                  : "Zdrojové trasy ještě nemají naskenované pečeti. Zkus obnovit až řidiči dokončí trasy."}
              </div>
            )}

            {/* Pečeť, kterou nikdo předem neohlásil, není chyba — ohlášené pečeti jsou
                jen kontrolní. Zůstává jako informace, ale neutrálně; dřív to byl
                varovný oranžový blok „Neznámé pečeti". */}
            {extra.length > 0 && (
              <div style={{
                background: "rgb(var(--c-elevated))",
                border: "1px solid rgb(var(--c-border))",
                borderRadius: 12,
                padding: "12px 16px",
                marginBottom: 8,
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "rgb(var(--c-text-soft))", marginBottom: 6 }}>
                  Nebylo ohlášeno předem ({extra.length})
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {extra.map((s) => (
                    <span key={s} style={{
                      padding: "3px 10px",
                      background: "rgb(var(--c-surface))",
                      border: "1px solid rgb(var(--c-border))",
                      borderRadius: 20,
                      fontSize: 12,
                      fontFamily: "monospace",
                      color: "rgb(var(--c-text-soft))",
                      fontWeight: 600,
                    }}>{s}</span>
                  ))}
                </div>
              </div>
            )}

            <div style={{ height: 80 }} />
          </div>
        )}
      </IonContent>

      {!isLoading && !isError && (
        <div style={{
          position: "fixed",
          bottom: 0, left: 0, right: 0,
          // spodní odsazení nad virtuálními tlačítky (inline shorthand by třídu přebil)
          padding: "12px 16px calc(env(safe-area-inset-bottom, 0px) + 12px)",
          background: "rgb(var(--c-surface))",
          borderTop: "1px solid rgb(var(--c-border))",
          display: "flex",
          gap: 8,
        }}>
          {missing.length > 0 && scanned.length > 0 && (
            <div style={{
              flex: "0 0 auto",
              display: "flex",
              alignItems: "center",
              fontSize: 12,
              color: "rgb(var(--c-muted))",
              fontWeight: 600,
            }}>
              {missing.length} neohlášeno
            </div>
          )}
          <button
            onClick={handleComplete}
            disabled={saving || scanned.length === 0}
            style={{
              flex: 1,
              padding: "14px 20px",
              background: "rgb(var(--c-success))",
              border: "none",
              borderRadius: 10,
              fontSize: 15,
              fontWeight: 700,
              color: "#fff",
              cursor: saving || scanned.length === 0 ? "not-allowed" : "pointer",
              opacity: saving || scanned.length === 0 ? 0.5 : 1,
            }}
          >
            {saving ? "Ukládám…" : btnLabel}
          </button>
        </div>
      )}

      {scannerOpen && (
        <BarcodeScanner
          onScan={handleScan}
          onClose={() => setScannerOpen(false)}
        />
      )}
    </IonPage>
  );
}
